import React, { useEffect, useRef, useState } from 'react';

interface SixDigitInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
}

const SixDigitInput: React.FC<SixDigitInputProps> = ({
  value,
  onChange,
  error = false,
  disabled = false,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    const newDigits = value ? value.split('').slice(0, 6) : Array(6).fill('');
    while (newDigits.length < 6) newDigits.push('');
    setDigits(newDigits);
  }, [value]);

  useEffect(() => {
    const firstEmpty = digits.findIndex((d) => d === '');
    const idx = firstEmpty === -1 ? 5 : firstEmpty;
    const ref = inputRefs.current[idx];
    if (ref) ref.focus();
  }, []);

  const updateDigits = (newDigits: string[]) => {
    setDigits(newDigits);
    onChange(newDigits.join(''));
  };

  const onInputChange = (index: number, raw: string) => {
    const char = raw.replace(/\D/g, '').slice(0, 1);
    const newDigits = [...digits];
    newDigits[index] = char;
    updateDigits(newDigits);
    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const onKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = [...digits];
      if (newDigits[index]) {
        newDigits[index] = '';
        updateDigits(newDigits);
        return;
      }
      if (index > 0) {
        newDigits[index - 1] = '';
        updateDigits(newDigits);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = pasted.split('');
    while (newDigits.length < 6) newDigits.push('');
    updateDigits(newDigits);
    const nextEmpty = newDigits.findIndex((d) => d === '');
    const idx = nextEmpty === -1 ? 5 : Math.min(nextEmpty, 5);
    inputRefs.current[idx]?.focus();
  };

  const inputStyle: React.CSSProperties = {
    width: '48px',
    height: '48px',
    textAlign: 'center',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    border: error ? '2px solid #f44336' : '1px solid #ccc',
    borderRadius: '8px',
    backgroundColor: disabled ? '#f5f5f5' : '#fff',
    color: disabled ? '#999' : '#000',
    outline: 'none',
  };

  return (
    <div 
      aria-label="6-digit-code" 
      role="group" 
      style={{ 
        display: 'flex', 
        gap: '12px', 
        justifyContent: 'center',
        margin: '16px 0' 
      }}
    >
      {digits.map((digit, i) => (
        <input
          key={i}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={digit}
          onChange={(e) => onInputChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          ref={(el) => {
            if (el) inputRefs.current[i] = el;
          }}
          aria-label={`Digit ${i + 1}`}
          disabled={disabled}
          style={inputStyle}
        />
      ))}
    </div>
  );
};

export default SixDigitInput;
